import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Row, Table, Radio, Button } from 'antd';
import ExportJsonExcel from 'js-export-excel';

import generatePath from '~settings/routing';
import formatPrice from '~helpers/currency';
import { communicationStatus } from '~client/constants';
import TransferredModal from '~pages/dashboard/billing/transfer/transferred-modal';
import { transferStatus } from '~constants/landlord';
import { dayFormatType, formatDay } from '~helpers/day';
import { bookingJourneyType } from 'common/constants/booking';
import { listOrderTransfersExport } from '~actions/billing';

@withTranslation()
export default class TransferTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentType: 'all',
      exportLoading: false,
    };
  }
  getColumns = () => {
    const { t } = this.props;
    const columns = [
      {
        title: t('cms.billing.transfer.table.transfer_number.field'),
        dataIndex: 'id',
        key: 'id',
        width: 185,
        render: (text, record) => (
          <span className="transfer__table-field">
            {record.node.referenceId ? record.node.referenceId : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.transfer.table.transfer_type.field'),
        dataIndex: 'transferType',
        key: 'transferType',
        width: 185,
        render: (text, record) => (
          <span className="transfer__table-field">
            {record.node.transferType
              ? t(
                  `cms.billing.transfer.table.transfer_type.${record.node.transferType.toLowerCase()}`,
                )
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.transfer.table.landlord.field'),
        dataIndex: 'landlordName',
        key: 'landlordName',
        width: 185,
        render: (text, record) => (
          <span className="transfer__table-field">
            {record.node.landlord ? record.node.landlord.name : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.transfer.table.property.field'),
        dataIndex: 'propertyName',
        key: 'propertyName',
        width: 205,
        render: (text, record) => (
          <span className="transfer__table-field">
            {record.node.order && record.node.order.property
              ? record.node.order.property.name
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.transfer.table.filter.payout_method.lable'),
        dataIndex: 'payoutMethod',
        key: 'payoutMethod',
        width: 150,
        render: (text, record) => (
          <div className="transfer__table-field">
            {record?.node?.actualTransferMethod
              ? t(
                  `cms.billing.transfer.details.receive_payment_method.${record.node.actualTransferMethod?.toLowerCase()}`,
                )
              : '-'}
          </div>
        ),
      },
      {
        title: t('cms.billing.transfer.table.booking_number.field'),
        dataIndex: 'bookingNumber',
        key: 'bookingNumber',
        width: 150,
        render: (text, record) => (
          <div className="transfer__table-field">
            {record.node.order ? record.node.order.referenceId : '-'}
          </div>
        ),
      },
      {
        title: t('cms.billing.transfer.table.planing_transfer_date.field'),
        dataIndex: 'planingTransferDate',
        key: 'planingTransferDate',
        width: 185,
        render: (text, record) => (
          <span className="transfer__table-field">
            {record.node.planningTransferDatetime
              ? formatDay(dayFormatType.UKFORMAT, record.node.planningTransferDatetime)
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.transfer.table.actual_transfer_date.field'),
        dataIndex: 'actualTransferDate',
        key: 'actualTransferDate',
        width: 185,
        render: (text, record) => (
          <span className="transfer__table-field">
            {record.node.actualTransferDatetime
              ? formatDay(dayFormatType.UKFORMAT, record.node.actualTransferDatetime)
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.transfer.table.transfer_amount.field'),
        dataIndex: 'amount',
        key: 'amount',
        width: 185,
        render: (text, record) => (
          <span className="transfer__table-field">
            {record.node.amount && record.node.currency
              ? formatPrice(record.node.amount, record.node.currency)
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.transfer.table.transfer_status.field'),
        dataIndex: 'status',
        key: 'status',
        width: 150,
        render: (text, record) => (
          <span className="transfer__table-field">
            {record.node.status
              ? t(`cms.billing.transfer.table.transfer_status.${record.node.status.toLowerCase()}`)
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.transfer.table.action.field'),
        key: 'action',
        fixed: 'right',
        align: 'center',
        width: 156,
        className: 'transfer__action',
        render: (text, record) => (
          <Row
            className={classNames('transfer__action-row', {
              'transfer__action-row--transferred':
                record.node.status === transferStatus.PENDING_TRANSFER,
            })}
          >
            <button
              className="transfer__table-btn"
              onClick={() => {
                this.handleViewBtn(record.node.referenceId);
              }}
            >
              {t('cms.billing.transfer.table.action.view.btn')}
            </button>
            <If condition={record.node.status === transferStatus.PENDING_TRANSFER}>
              <TransferredModal
                t={t}
                onConfirm={(transactionNo, onSuccess) =>
                  this.handleConfirm(record.node.id, transactionNo, onSuccess)
                }
                communication={this.props.communication}
                type="tableBtn"
              />
            </If>
          </Row>
        ),
      },
    ];

    return columns;
  };

  handleConfirm = (id, transactionNo, onSuccess = () => {}) => {
    const { communication } = this.props;
    if (
      communication &&
      communication.updateOrderTransferred &&
      communication.updateOrderTransferred.status !== communicationStatus.FETCHING
    ) {
      this.props.updateOrderTransferStatus(id, transactionNo, onSuccess);
    }
  };

  handleViewBtn = id => {
    this.props.history.push(generatePath('billing.transferDetail', { id }));
  };

  handleSetRadioValue = e => {
    const value = e?.target?.value;
    this.setState({
      currentType: value,
    });
    this.props.handleTabChange(value);
  };

  handleExportToExcel = exportData => {
    const sheetHeader = [
      'Booking Number',
      'Transfer Number',
      'Transfer Type',
      'Landlord',
      'Property',
      'Currency',
      'Transfer Amount',
      'Planing Transfer date',
      'Actual Transfer date',
      'Transfer Status',
      'Student Name',
      'Move in date',
      'Move out date',
      'Total Rent',
      'Commission',
      'AP Category',
    ];

    const receiveTransferPage = {
      ALL: 'All',
      DEPOSIT: 'Deposit',
      FIRST_RENTAL: 'First Rental',
    };

    // const receivePaymentMethod = {
    //   ALL: 'All',
    //   STRIPE: 'Stripe',
    //   BANK_TRANSFER: 'Bank transfer',
    //   VIRTUAL_CREDIT_CARD: 'Virtual credit card',
    // };

    const receiveTransferStatus = {
      NEW: 'New',
      ALL: 'All',
      PENDING_TRANSFER: 'Pending Transfer',
      TRANSFERRED: 'Transferred',
      VOID: 'Void',
      SUSPEND: 'Suspend',
    };

    const xlsxData = exportData.map(data => {
      const transferType = data?.node?.transferType
        ? receiveTransferPage?.[data?.node?.transferType]
        : '';
      // const payoutMethod = data?.node?.actualTransferMethod
      //   ? receivePaymentMethod?.[data?.node?.actualTransferMethod]
      //   : '';
      const transferStatus = data?.node?.status ? receiveTransferStatus?.[data?.node?.status] : '';
      return {
        bookingNumber: data?.node?.order?.referenceId,
        transferNumber: data?.node?.referenceId,
        transferType,
        landlord: data?.node?.landlord?.name,
        property: data?.node?.order?.property?.name,
        currency: data?.node?.currency,
        transferAmount: data?.node?.amount,
        // payoutMethod,
        planingTransferDate: data?.node?.planningTransferDatetime
          ? formatDay(dayFormatType.UKFORMAT, data?.node?.planningTransferDatetime)
          : '',
        actualTransferDate: data?.node?.actualTransferDatetime
          ? formatDay(dayFormatType.UKFORMAT, data?.node?.actualTransferDatetime)
          : '',
        transferStatus,
        studentName:
          data?.node?.order?.student?.passportFirstName ||
          data?.node?.order?.student?.passportLastName
            ? `${data?.node?.order?.student?.passportFirstName} ${data?.node?.order?.student?.passportLastName}`
            : '-',
        moveInDate: data?.node?.order?.moveInDate
          ? formatDay(dayFormatType.UKFORMAT, data?.node?.order?.moveInDate)
          : '-',
        moveOutDate: data?.node?.order?.moveOutDate
          ? formatDay(dayFormatType.UKFORMAT, data?.node?.order?.moveOutDate)
          : '-',
        totalRent: data?.node?.order?.totalRental,
        commission: data?.node?.order?.commissions?.[0]?.totalPrice ?? '-',
        apCategory: data?.node?.order?.landlordApCategory
          ? this.props.t(
              `cms.landlord.modal.ap_category.${data?.node?.order?.landlordApCategory?.toLowerCase()}.option`,
            )
          : '-',
      };
    });
    const option = {};
    option['fileName'] = 'Transfer Management';

    option['datas'] = [
      {
        sheetData: xlsxData,
        sheetHeader,
      },
    ];
    var toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
    this.setState({ exportLoading: false });
  };

  handleExport = () => {
    this.setState({ exportLoading: true });
    listOrderTransfersExport(
      {
        ...this.props.filters,
        pageNumber: 1,
        pageSize: this.props.list?.total ?? 10,
        bookingJourneys: !this.props.filters?.bookingJourney
          ? [bookingJourneyType.SEMI_AUTOMATIC, bookingJourneyType.INSTANT_BOOK]
          : this.props.filters?.bookingJourneys,
      },
      res => {
        if (res.listOrderTransfers?.edges?.length > 0) {
          this.handleExportToExcel(res.listOrderTransfers?.edges);
        }
      },
      () => {
        this.setState({ exportLoading: false });
      },
    );
  };

  render() {
    const { t, list, communication } = this.props;
    const { currentType, exportLoading } = this.state;
    return (
      <div className="transfer__table-content">
        <div className="transfer__table-header-container">
          <Radio.Group onChange={this.handleSetRadioValue} value={currentType}>
            <Radio.Button value="all">
              {t('cms.properties.edit.rooms.filter.updated_time.all')}
            </Radio.Button>
            <Radio.Button value={bookingJourneyType.SEMI_AUTOMATIC}>
              {t('properties_booking_type_semi_automatic')}
            </Radio.Button>
            <Radio.Button value={bookingJourneyType.INSTANT_BOOK}>
              {t('properties_booking_type_instant_book')}
            </Radio.Button>
          </Radio.Group>
          <div className="transfer__table__left-wrapper">
            <h4 className="transfer__table-tltle">
              {t('cms.billing.transfer.table.total_title', { totalCount: list.total || 0 })}
            </h4>
            <If condition={list?.total !== 0}>
              <Button
                loading={exportLoading}
                icon="export"
                className="transfer__table__left-wrapper__export"
                onClick={this.handleExport}
              >
                {t('cms.referral.partner.header.btn.export')}
              </Button>
            </If>
          </div>
        </div>
        <Table
          className="transfer__table"
          columns={this.getColumns()}
          dataSource={list.payload || []}
          loading={
            communication &&
            communication.transferList &&
            communication.transferList.status === communicationStatus.FETCHING
          }
          pagination={{
            current: this.props.filters.pageNumber,
            pageSize: this.props.filters.pageSize,
            showSizeChanger: true,
            hideOnSinglePage: false,
            total: list.total,
            onChange: this.props.handlePaginationChange,
            onShowSizeChange: this.props.handlePageSizeChange,
          }}
        />
      </div>
    );
  }
}

TransferTable.propTypes = {
  t: PropTypes.func.isRequired,
  list: PropTypes.object,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  communication: PropTypes.object,
  filters: PropTypes.object,
  handlePaginationChange: PropTypes.func,
  handlePageSizeChange: PropTypes.func,
  handleTabChange: PropTypes.func,
  updateOrderTransferStatus: PropTypes.func.isRequired,
};

TransferTable.defaultProps = {
  t: () => {},
  list: {},
  history: {
    push: () => {},
  },
  communication: {},
  filters: {},
  handlePaginationChange: () => {},
  handlePageSizeChange: () => {},
  handleTabChange: () => {},
  updateOrderTransferStatus: () => {},
};
